/* eslint-disable @typescript-eslint/no-explicit-any */
import { $idTaggingEnabled, $lang, ApiError, useApiClient, useAsyncCache, useTranslation } from '@msdk/common'
import { AboutMicroservice } from '@msdk/definitions'
import { getMicroserviceType, MicroserviceType } from '@msdk/definitions/src/definitions/enums/microservice-type'
import {
  Box,
  COLORS,
  createComponentWrapperWithRef,
  Divider,
  Link,
  ListItemText,
  styled,
  Typography,
  uiTokens,
} from '@msdk/ui'
import AboutCustomView from 'components/AboutCustomView'
import AboutModal from 'components/AboutModal/AboutModal'
import { formatLineReturn } from 'components/AboutModal/utils'
import { FullScreenLoader } from 'components/FullScreenLoader'
import NoResultIcon from 'components/Icons/NoResultIcon'
import React, { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { MAX_INT } from 'utils'

const StyledNoResultBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`
const StyledLinkBox = styled(Box)`
  display: flex;
  align-items: baseline;
  gap: 5px;
`
const StyledVersionMuiTypography = styled(Typography)`
  opacity: 60%;
  max-width: 180px;
  overflow-x: hidden;
  text-overflow: ellipsis;
`
const StyledVersionNovaTypography = styled(Typography)`
  color: ${uiTokens.uiContent.neutral};
  text-overflow: ellipsis;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: ${uiTokens.font.lineHeight.uiControlText.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal};
`
const StyledVersionTypography = createComponentWrapperWithRef(
  {
    mui: StyledVersionMuiTypography,
    nova: StyledVersionNovaTypography,
    muiProps: { variant: 'body2' },
  },
  'StyledVersionTypography',
)

const StyledMicroserviceMuiLink = styled(Link)`
  text-decoration: none;
  margin: 0;
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`
const StyledMicroserviceNovaLink = styled(Link)`
  margin: 0;
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`
const StyledMicroserviceLink = createComponentWrapperWithRef(
  {
    mui: StyledMicroserviceMuiLink,
    nova: StyledMicroserviceNovaLink,
  },
  'StyledMicroserviceLink',
)

const StyledMicroserviceLinkTypography = styled(Typography)`
  text-decoration: none;
  margin: 0;
  /* max-width: 85%; */
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`
const StyledMuiSpacer = styled(Box)`
  padding-bottom: 40px;
`
const StyledNovaSpacer = styled(Box)`
  padding-bottom: 24px;
`
const StyledSpacer = createComponentWrapperWithRef(
  {
    mui: StyledMuiSpacer,
    nova: StyledNovaSpacer,
  },
  'StyledSpacer',
)
const StyledNonMedicalMuiMicroserviceNameTypography = styled(Typography)`
  text-decoration: none;
  margin: 0;
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`
const StyledNonMedicalNovaMicroserviceNameTypography = styled(Typography)`
  overflow: hidden;
  color: ${uiTokens.color.basic.black};
  text-overflow: ellipsis;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: ${uiTokens.font.lineHeight.uiControlText.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal};
`
const StyledNonMedicalMicroserviceNameTypography = createComponentWrapperWithRef(
  {
    mui: StyledNonMedicalMuiMicroserviceNameTypography,
    nova: StyledNonMedicalNovaMicroserviceNameTypography,
    muiProps: { variant: 'body1' },
  },
  'StyledNonMedicalMicroserviceNameTypography',
)

const StyledListItemMuiText = styled(ListItemText)`
  max-width: 100%;
  overflow-x: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
`
const StyledListItemNovaText = styled(ListItemText)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
  & .MuiListItemText-secondary {
    color: ${uiTokens.uiContent.neutral};
    font-size: ${uiTokens.font.fontSize.uiControlText.large}px;
    font-style: normal;
    font-weight: ${uiTokens.font.fontWeight.medium};
    line-height: ${uiTokens.font.lineHeight.uiControlText.large}px;
    letter-spacing: ${uiTokens.font.letterSpacing.normal};
    text-overflow: ellipsis;
  }
`
const StyledListItemText = createComponentWrapperWithRef(
  {
    mui: StyledListItemMuiText,
    nova: StyledListItemNovaText,
  },
  'StyledListItemText',
)

const HeaderWrapperMui = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  min-height: 88px;
  padding: 32px 32px 0;
  margin-bottom: 8px;
`
const HeaderContainerMui = styled('div')`
  flex-direction: row;
  display: inline-flex;
`
const HeaderWrapperNova = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  min-height: 88px;
  background: ${uiTokens.uiSurface.interactive};
  padding: ${uiTokens.spacing.padding.large}px ${uiTokens.spacing.padding.large}px 0 ${uiTokens.spacing.padding.large}px;
  border-bottom: ${uiTokens.sizing.stroke.medium}px solid ${uiTokens.uiStroke.neutral};
`
const HeaderContainerNova = styled('div')`
  flex-direction: row;
  display: inline-flex;
  background: ${uiTokens.uiSurface.interactive};
  padding-bottom: ${uiTokens.spacing.padding.large}px;
`
const HeaderWrapper = createComponentWrapperWithRef(
  {
    mui: HeaderWrapperMui,
    nova: HeaderWrapperNova,
  },
  'HeaderWrapper',
)

const HeaderContainer = createComponentWrapperWithRef(
  {
    mui: HeaderContainerMui,
    nova: HeaderContainerNova,
  },
  'HeaderContainer',
)

const StyledAboutTitle = createComponentWrapperWithRef(
  {
    mui: Typography,
    nova: Typography,
    muiProps: { variant: 'h2' },
    novaProps: { variant: 'h3' },
  },
  'StyledAboutTitle',
)

const StyledContainerBox = createComponentWrapperWithRef<{ children: React.ReactNode }>(
  {
    mui: ({ children }) => <Box sx={{ padding: 4 }}>{children}</Box>,
    nova: ({ children }) => <Box sx={{ padding: 3 }}>{children}</Box>,
  },
  'StyledContainerBox',
)

const StyledNovaContentBox = styled(Box)<{ $showBorder?: boolean }>`
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: ${uiTokens.spacing.padding.small}px;
  border-bottom: ${({ $showBorder }) =>
    $showBorder ? `${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral}` : 'none'};
`
const StyledContentBox = createComponentWrapperWithRef<{ children: React.ReactNode; showBorder?: boolean }>(
  {
    mui: ({ children }) => (
      <Box paddingBottom={2} paddingTop={2}>
        {children}
      </Box>
    ),
    nova: ({ children, showBorder }) => (
      <StyledNovaContentBox $showBorder={showBorder}>{children}</StyledNovaContentBox>
    ),
  },
  'StyledContentBox',
)
const StyledNovaSubContent = styled(Box)`
  padding: ${uiTokens.spacing.padding.semiMedium}px 8px;
  gap: ${uiTokens.spacing.gap.small}px;
`
const StyledSubContent = createComponentWrapperWithRef<{ children: React.ReactNode }>(
  {
    mui: ({ children }) => <div>{children}</div>,
    nova: StyledNovaSubContent,
  },
  'StyledSubContent',
)

const StyledNovaDivider = styled('div')`
  display: none;
`
const StyledDivider = createComponentWrapperWithRef<{ children?: React.ReactNode }>(
  {
    mui: Divider,
    nova: StyledNovaDivider,
  },
  'StyledDivider',
)
const StyledTypographyProducts = createComponentWrapperWithRef(
  {
    mui: Typography,
    nova: Typography,
    muiProps: { variant: 'h5', color: COLORS.text.secondary },
    novaProps: { variant: 'h6', color: uiTokens.uiContent.emphasized, fontWeight: uiTokens.font.fontWeight.semiBold },
  },
  'StyledTypographyProduct',
)
const StyledNonMedicalBox = createComponentWrapperWithRef(
  {
    mui: Box,
    nova: Box,
    muiProps: { mt: 4 },
  },
  'StyledNonMedicalBox',
)

const StyledTypographyProductWrapper = createComponentWrapperWithRef(
  {
    mui: Box,
    nova: Box,
    muiProps: { mb: 4 },
    novaProps: { mb: 1 },
  },
  'StyledTypographyProductsWrapper',
)

const AboutPage = () => {
  const cache = useAsyncCache()
  const client = useApiClient()
  const userLanguage = useRecoilValue($lang)
  const idTaggingEnabled = useRecoilValue($idTaggingEnabled)

  const [isLoading, setIsLoading] = useState(true)

  const [medicalProducts, setMedicalProducts] = useState<AboutMicroservice[] | null>(null)
  const [nonMedicalProducts, setNonMedicalProducts] = useState<AboutMicroservice[] | null>(null)
  const [services, setServices] = useState<AboutMicroservice[] | null>(null)

  const [open, setOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const t = useTranslation()
  const handleClickOpen = (item: any) => {
    setSelectedItem(item)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedItem(null)
  }

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)
      const paramsObj = new URLSearchParams()
      if (idTaggingEnabled) {
        paramsObj.append('idTaggingEnabled', 'true')
      }
      const params = paramsObj.size > 0 ? `?${paramsObj.toString()}` : ''

      await cache
        .getOrCreate(`microservices${params}`, async () => {
          // Commenting following JWT login due to deprecation in favor of Ref token
          // client.setJwtToken()
          const res = await client.read<AboutMicroservice[]>(
            `CMPS/api/Composer/aboutinformation/${userLanguage}${params}`,
          )
          return res
        })
        .catch((error: ApiError) => {
          console.error(error)
        })
        .then((microservices) => {
          if (microservices && !(microservices instanceof ApiError) && microservices.length > 0) {
            const sortedMicroservices = microservices.sort((a, b) => {
              const positionA = a.position === 0 ? MAX_INT : a.position ?? MAX_INT
              const positionB = b.position === 0 ? MAX_INT : b.position ?? MAX_INT

              if (positionA !== positionB) {
                return positionA - positionB
              }

              const nameA = a.productName || a.name || ''
              const nameB = b.productName || b.name || ''
              return nameA.localeCompare(nameB)
            })
            setServices(
              sortedMicroservices.filter(
                (microservice) =>
                  !microservice.msType || getMicroserviceType(microservice!.msType) == MicroserviceType.Service,
              ),
            )
            setMedicalProducts(
              sortedMicroservices.filter(
                (microservice) =>
                  microservice.msType &&
                  getMicroserviceType(microservice.msType) == MicroserviceType.Product &&
                  microservice.medicalDevice,
              ),
            )
            setNonMedicalProducts(
              sortedMicroservices.filter(
                (microservice) =>
                  microservice.msType &&
                  getMicroserviceType(microservice.msType) == MicroserviceType.Product &&
                  !microservice.medicalDevice,
              ),
            )
          }
        })
        .then(() => setIsLoading(false))
    }
    fetch().catch((error) => console.error(error))
  }, [idTaggingEnabled])

  if (isLoading) {
    return <FullScreenLoader />
  }

  return (
    <>
      <HeaderWrapper>
        <HeaderContainer>
          <StyledAboutTitle>{t('ABOUT_SIDEBAR_LBL', 'About')}</StyledAboutTitle>
        </HeaderContainer>
      </HeaderWrapper>
      <Box>
        {(medicalProducts || nonMedicalProducts || services) && (
          <StyledContainerBox>
            {(medicalProducts!.length > 0 || nonMedicalProducts!.length > 0) && (
              <AboutCustomView title={t('ABOUT_PRODUCTS_LBL', 'Products')} type="products">
                {medicalProducts && medicalProducts.length > 0 && (
                  <Box>
                    <Box>
                      {medicalProducts.map((item, index) => (
                        <React.Fragment key={index}>
                          <StyledContentBox
                            showBorder={
                              medicalProducts.length > 0 &&
                              !(nonMedicalProducts?.length === 0 && index === medicalProducts.length - 1)
                            }
                          >
                            <StyledSubContent>
                              {index === 0 && (
                                <>
                                  <StyledTypographyProductWrapper>
                                    <StyledTypographyProducts>
                                      {t('ABOUT_MEDICAL_LBL', 'Medical')}
                                    </StyledTypographyProducts>
                                  </StyledTypographyProductWrapper>
                                </>
                              )}
                              <StyledLinkBox>
                                {item.popUp ? (
                                  <StyledMicroserviceLink onClick={() => (item.popUp ? handleClickOpen(item) : null)}>
                                    {(item.productName && formatLineReturn(item.productName)) ||
                                      (item.name && formatLineReturn(item.name))}
                                  </StyledMicroserviceLink>
                                ) : (
                                  <StyledNonMedicalMicroserviceNameTypography>
                                    {(item.productName && formatLineReturn(item.productName)) ||
                                      (item.name && formatLineReturn(item.name))}
                                  </StyledNonMedicalMicroserviceNameTypography>
                                )}
                                {item.version && (
                                  <StyledVersionTypography>
                                    {item.version ? `v${item.version}` : null}
                                  </StyledVersionTypography>
                                )}
                              </StyledLinkBox>
                              <StyledListItemText secondary={item.description && formatLineReturn(item.description)} />
                            </StyledSubContent>
                          </StyledContentBox>

                          {index < medicalProducts.length - 1 && <StyledDivider />}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                )}
                {medicalProducts && medicalProducts.length > 0 && <StyledDivider />}
                {nonMedicalProducts && nonMedicalProducts.length > 0 && (
                  <StyledNonMedicalBox>
                    <Box>
                      {nonMedicalProducts.map((item, index) => (
                        <React.Fragment key={index}>
                          <StyledContentBox showBorder={index < nonMedicalProducts.length - 1}>
                            <StyledSubContent>
                              {index === 0 && (
                                <>
                                  <StyledTypographyProductWrapper>
                                    <StyledTypographyProducts gutterBottom>
                                      {t('ABOUT_NON_MEDICAL_LBL', 'Non-medical')}
                                    </StyledTypographyProducts>
                                  </StyledTypographyProductWrapper>
                                </>
                              )}
                              <StyledLinkBox>
                                {item.popUp ? (
                                  <StyledMicroserviceLink onClick={() => (item.popUp ? handleClickOpen(item) : null)}>
                                    {(item.productName && formatLineReturn(item.productName)) ||
                                      (item.name && formatLineReturn(item.name))}
                                  </StyledMicroserviceLink>
                                ) : (
                                  <StyledNonMedicalMicroserviceNameTypography>
                                    {(item.productName && formatLineReturn(item.productName)) ||
                                      (item.name && formatLineReturn(item.name))}
                                  </StyledNonMedicalMicroserviceNameTypography>
                                )}
                                {item.version && (
                                  <StyledVersionTypography>
                                    {item.version ? `v${item.version}` : null}
                                  </StyledVersionTypography>
                                )}
                              </StyledLinkBox>
                              <StyledListItemText secondary={item.description && formatLineReturn(item.description)} />
                            </StyledSubContent>
                          </StyledContentBox>
                          {index < nonMedicalProducts.length - 1 && <StyledDivider />}
                        </React.Fragment>
                      ))}
                    </Box>
                  </StyledNonMedicalBox>
                )}
              </AboutCustomView>
            )}
            {(medicalProducts!.length > 0 || nonMedicalProducts!.length > 0) && <StyledSpacer />}
            {services!.length > 0 && (
              <AboutCustomView title={t('ABOUT_SERVICES_LBL', 'Services')} type="services">
                {services && services.length > 0 && (
                  <Box>
                    {services.map((item, index) => (
                      <React.Fragment key={index}>
                        <StyledContentBox showBorder={index < services.length - 1}>
                          <StyledSubContent>
                            <StyledLinkBox>
                              <StyledNonMedicalMicroserviceNameTypography>
                                {item.productName || item.name}
                              </StyledNonMedicalMicroserviceNameTypography>
                              <StyledVersionTypography>
                                {item.version ? `v${item.version}` : null}
                              </StyledVersionTypography>
                            </StyledLinkBox>
                            <StyledListItemText secondary={item.description && formatLineReturn(item.description)} />
                          </StyledSubContent>
                        </StyledContentBox>
                        {index < services.length - 1 && <StyledDivider />}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </AboutCustomView>
            )}
            <AboutModal open={open} onClose={handleClose} microservice={selectedItem} />
          </StyledContainerBox>
        )}
        {!medicalProducts && !nonMedicalProducts && !services && (
          <StyledNoResultBox>
            <NoResultIcon />
            <Typography variant="h5" gutterBottom>
              {t('ABOUT_NO_RESULTS', 'Sorry, no information available.')}
            </Typography>
          </StyledNoResultBox>
        )}
      </Box>
    </>
  )
}

export default AboutPage
